/* ------------------------------------------------------------ Settings */

/** Replace with the real checkout link. */
export const CHECKOUT_URL = 'https://resin-to-profit.impultienda.ar/checkout'

export const BRAND_COMPANY = 'Redondo Land'

export const BRAND = 'Resin to Profit™'

export const CTA_LABEL = 'Get Resin to Profit™ now →'

/**
 * Real launch deadline (ISO date). Leave as null to hide the countdown.
 * Example: '2026-10-15T23:59:00-07:00'
 */
export const LAUNCH_DEADLINE: string | null = null

export const pricing = {
  regularPrice: '$47 USD',
  today: '$27',
  todayValue: 27,
  idealFor:
    'Beginners who want to create attractive resin products and understand how to organize them as a small handmade business.',
}

/* -------------------------------------------------------------- Images */

export type Crop = { x: number; y: number; w: number; h: number }

export type ImageAsset = {
  src: string
  width: number
  height: number
  alt: string
  /** Pixel region of the source image to show. Omit to show the whole image. */
  crop?: Crop
}

const W = 1122
const H = 1402

const IMG_PRODUCTS_FLATLAY =
  'https://cdn.magicpatterns.com/uploads/oR6SpEhybUakXKopE2ZBqt/Imagen_de_ChatGPT_27_sept_2026,_19_05_01-6.png'
const IMG_HANDOFF =
  'https://cdn.magicpatterns.com/uploads/2qhrBsHDkKPgZPJxiBJFK4/Imagen_de_ChatGPT_27_sept_2026,_19_04_54-4.png'
const IMG_BULK_ORDER =
  'https://cdn.magicpatterns.com/uploads/k6ufKYaRKhze4rmeKZYY4Q/Imagen_de_ChatGPT_27_sept_2026,_19_04_53-3.png'
const IMG_BONUS =
  'https://cdn.magicpatterns.com/uploads/qrSX7L7Qmcd1SQqvTBGPJ4/Imagen_de_ChatGPT_27_sept_2026,_19_04_51-2.png'
const IMG_MAIN =
  'https://cdn.magicpatterns.com/uploads/8hAELNoAGNzUkkNnwdvWSK/Imagen_de_ChatGPT_27_sept_2026,_19_04_49-1.png'
const IMG_MAIN_DARK =
  'https://cdn.magicpatterns.com/uploads/2Lq5J1cSev5gpcUSjK6JRK/Imagen_de_ChatGPT_27_sept_2026,_16_11_55.png'

export const MAIN_MOCKUP: ImageAsset = {
  src: IMG_MAIN,
  width: W,
  height: H,
  alt: 'Resin to Profit™ — the complete 6-volume system with finished resin keychains, pet tag, pen and bookmark',
}

/** Main hero image — shown uncropped. */
export const HERO_MOCKUP: ImageAsset = {
  src: 'https://cdn.magicpatterns.com/uploads/eY5HiZh9qKubKnPChrE35B/mock_up_principal_(2).png',
  width: W,
  height: H,
  alt: 'Resin to Profit™ — all six volumes with personalized resin pens, pet tag, photo keychain, bookmark and baby favors',
}

/** Main mockup shown in every purchase block. */
export const PURCHASE_MOCKUP: ImageAsset = HERO_MOCKUP

export const MAIN_MOCKUP_DARK: ImageAsset = {
  src: IMG_MAIN_DARK,
  width: W,
  height: H,
  alt: 'All six Resin to Profit™ volumes with personalized resin pens, pet tags, bookmarks and keepsakes',
}

export const BONUS_MOCKUP: ImageAsset = {
  src: IMG_BONUS,
  width: W,
  height: H,
  alt: 'Bonus: Custom Order Client Kit — ready-to-use scripts and customer templates for personalized resin orders',
}

export const TRANSITION_IMAGE: ImageAsset = {
  src: IMG_HANDOFF,
  width: W,
  height: H,
  alt: 'A resin maker handing a finished personalized gift box to a smiling customer',
}

export const TRANSITION_LINE = 'Made by you. Wrapped with care. Handed to someone who asked for it.'

/* ---------------------------------------------------------------- Hero */

export const HERO_EYEBROW = 'The Complete Resin Creation & Business System'

export const HERO_HEADLINE_LEAD = 'Stop guessing.'
export const HERO_HEADLINE_ACCENT = 'Start creating resin products'
export const HERO_HEADLINE_TAIL = 'you’re proud to sell.'

export const HERO_SUB =
  'Go from confused beginner to creating polished resin pieces, fixing common mistakes, pricing your work and handling custom orders — with one complete step-by-step system.'

export const journeySteps: string[] = ['Learn', 'Create', 'Design', 'Fix', 'Price', 'Organize']

/* --------------------------------------------------------------- Proof */

export type Stat = { value: string; label: string }

export const stats: Stat[] = [
  { value: '6', label: 'Complete volumes' },
  { value: '20', label: 'Step-by-step projects' },
  { value: '50', label: 'Design recipes' },
  { value: '1', label: 'Custom order bonus' },
]

export type Testimonial = {
  initials: string
  quote: string
  name: string
  role: string
}

/** Editable placeholders — replace with real customer reviews. */
export const testimonials: Testimonial[] = [
  {
    initials: 'JM',
    quote:
      'I had saved so many resin videos that I didn’t even know where to start anymore. Having everything organized made it much easier to finally make something.',
    name: 'Jessica M.',
    role: 'Resin beginner',
  },
  {
    initials: 'KR',
    quote:
      'The troubleshooting guide is the part I keep coming back to. Before this, when a piece came out sticky or full of bubbles, I had no idea what I had done wrong.',
    name: 'Kayla R.',
    role: 'Hobby maker',
  },
  {
    initials: 'DL',
    quote:
      'I originally wanted to make keychains and personalized gifts. The pricing section made me realize I had never actually calculated what each piece was costing me.',
    name: 'Danielle L.',
    role: 'Custom gifts',
  },
]

/** Second block — editable placeholders. */
export const testimonialsSecond: Testimonial[] = [
  {
    initials: 'AB',
    quote:
      'I kept buying supplies and then just staring at them. Starting with Volume 1 and then picking a project from the Lab finally got me pouring.',
    name: 'Ashley B.',
    role: 'First-time pourer',
  },
  {
    initials: 'MT',
    quote:
      'My coasters kept coming out cloudy. I looked it up in Resin Fix, changed two things and the next batch was clear.',
    name: 'Megan T.',
    role: 'Weekend crafter',
  },
  {
    initials: 'SP',
    quote:
      'The order sheets alone were worth it for me. I used to track names and colors in my DMs. Now it’s all on one page.',
    name: 'Sarah P.',
    role: 'Takes custom orders',
  },
]

/* ---------------------------------------------------------------- Pain */

export const PAIN_HEADLINE =
  'How many more resin projects are you going to start by guessing?'

export const PAIN_INTRO: string[] = [
  'You see a beautiful resin piece and think: “I could make that.”',
  'Then you actually sit down to try.',
]

export const painQuestions: string[] = [
  'Which resin do you buy?',
  'What information matters on the label?',
  'Which pigments can you use?',
  'Why are there bubbles?',
  'Why is the piece still sticky?',
  'What should you make first?',
  'What should you charge?',
  'How do you handle a personalized order?',
]

export const PAIN_EMPHASIS_LEAD = 'The problem isn’t that resin is impossible.'

export const PAIN_EMPHASIS =
  'The problem is trying to learn everything from disconnected videos, screenshots and random advice.'

export const painLines: string[] = [
  'Buying materials before knowing what matters',
  'Watching tutorial after tutorial without a clear learning path',
  'Saving hundreds of ideas but never knowing what to make first',
  'Getting bubbles, sticky pieces or cloudy finishes and not knowing why',
  'Guessing which colors and effects will work together',
  'Guessing prices instead of understanding product cost',
  'Keeping orders, inventory and customer details scattered everywhere',
]

export const PAIN_CLOSING =
  'What if everything you needed was finally organized in one place?'

/* -------------------------------------------------------------- Reveal */

export const REVEAL_EYEBROW = 'The complete 6-volume system'

export const REVEAL_TITLE_LINE_1 = '6 volumes.'
export const REVEAL_TITLE_LINE_2 = 'One clear path.'

export const revealLines: string[] = [
  'No more random videos.',
  'No more wondering what to make next.',
  'No more guessing what went wrong.',
]

export const REVEAL_BODY =
  'Follow one connected system from your first resin piece to a more organized way to create, price and handle your products.'

/* ------------------------------------------------------------- Volumes */

export type Volume = {
  number: string
  title: string
  subtitle: string
  intro: string
  items: string[]
  /** Spine color of the physical volume, used as a small swatch. */
  swatch: 'blush' | 'beige' | 'mist' | 'pearl' | 'ivory'
}

export const volumes: Volume[] = [
  {
    number: '01',
    title: 'The Resin Starter Guide',
    subtitle: 'From zero to your first finished resin piece',
    intro: 'Learn the foundations before you pour:',
    items: [
      'Resin systems',
      'Manufacturer instructions',
      'Materials and tools',
      'Pigments and inclusions',
      'Mixing fundamentals',
      'Bubble prevention',
      'Curing',
      'Demolding',
      'Finishing',
      'Final assembly',
    ],
    swatch: 'ivory',
  },
  {
    number: '02',
    title: 'The Resin Project Lab',
    subtitle: '20 step-by-step products to create',
    intro:
      'Stop wondering what to make next. Follow 20 practical resin projects with materials, design direction and clear creation steps.',
    items: [],
    swatch: 'blush',
  },
  {
    number: '03',
    title: 'Resin Design Recipes',
    subtitle: '50 ready-to-follow color & design combinations',
    intro: 'Explore coordinated combinations of:',
    items: ['Colors', 'Effects', 'Metallic details', 'Inclusions', 'Finishes', 'Product styles'],
    swatch: 'beige',
  },
  {
    number: '04',
    title: 'Resin Fix',
    subtitle: 'The troubleshooting guide',
    intro: 'Know what to investigate when something goes wrong. Includes:',
    items: [
      'Sticky resin',
      'Bubbles',
      'Cloudiness',
      'Pigment issues',
      'Floating inclusions',
      'Layer problems',
      'Demolding issues',
      'Finishing mistakes',
      'Common beginner problems',
    ],
    swatch: 'mist',
  },
  {
    number: '05',
    title: 'The Resin Business Blueprint',
    subtitle: 'Cost. Price. Sell.',
    intro: 'Understand:',
    items: [
      'Product costs',
      'Pricing',
      'Margins',
      'Customization',
      'Presentation',
      'Product listings',
      'Selling channels',
      'Business basics',
    ],
    swatch: 'pearl',
  },
  {
    number: '06',
    title: 'Business Tools & Printables',
    subtitle: 'The ready-to-use resin business toolkit',
    intro: 'Practical resources for:',
    items: [
      'Pricing',
      'Orders',
      'Production',
      'Inventory',
      'Product planning',
      'Customer details',
      'Business organization',
    ],
    swatch: 'mist',
  },
]

/* ------------------------------------------ Carousel 1: what you create */

export const CREATE_TITLE = 'Look at what you could start creating.'

export const CREATE_SUB =
  'Personalized pieces, gifts, event favors and custom resin products that actually make you want to sit down and create.'

export type ProductShot = { label: string; image: ImageAsset }

export const productShots: ProductShot[] = [
  {
    label: 'Personalized pens',
    image: {
      src: IMG_BULK_ORDER,
      width: W,
      height: H,
      alt: 'Six personalized resin pens in ivory, blush and pale blue with gold names',
      crop: { x: 50, y: 240, w: 640, h: 800 },
    },
  },
  {
    label: 'Pet tags',
    image: {
      src: IMG_PRODUCTS_FLATLAY,
      width: W,
      height: H,
      alt: 'Pale blue marbled resin bone-shaped pet tag engraved with the name Spike',
      crop: { x: 420, y: 40, w: 420, h: 525 },
    },
  },
  {
    label: 'Event favors',
    image: {
      src: IMG_PRODUCTS_FLATLAY,
      width: W,
      height: H,
      alt: 'Baptism resin favors with pale blue ribbons and gold crosses',
      crop: { x: 20, y: 440, w: 460, h: 575 },
    },
  },
  {
    label: 'Keychains',
    image: {
      src: IMG_PRODUCTS_FLATLAY,
      width: W,
      height: H,
      alt: 'Initial A resin keychain with a blush tassel and gold charm',
      crop: { x: 0, y: 40, w: 460, h: 575 },
    },
  },
  {
    label: 'Bookmarks',
    image: {
      src: IMG_PRODUCTS_FLATLAY,
      width: W,
      height: H,
      alt: 'Ivory and blush resin bookmark reading “Just one more chapter” with a tassel',
      crop: { x: 682, y: 30, w: 440, h: 550 },
    },
  },
  {
    label: 'Keepsakes',
    image: {
      src: IMG_PRODUCTS_FLATLAY,
      width: W,
      height: H,
      alt: 'Round resin photo keychain of a boy hugging his golden retriever, framed in pearl flakes',
      crop: { x: 515, y: 460, w: 420, h: 525 },
    },
  },
  {
    label: 'Gift sets',
    image: {
      src: IMG_PRODUCTS_FLATLAY,
      width: W,
      height: H,
      alt: 'Gift box with a resin trinket tray, heart jewelry dish and matching resin pen',
      crop: { x: 200, y: 700, w: 560, h: 700 },
    },
  },
  {
    label: 'Custom orders',
    image: {
      src: IMG_BULK_ORDER,
      width: W,
      height: H,
      alt: 'A finished custom order of named resin pens and keychains packed in a gift box',
    },
  },
]

/* ---------------------------------------------- Carousel 2: inside pages */

export const INSIDE_TITLE = 'Inside Resin to Profit™'

export const INSIDE_SUB = 'See exactly what you’re getting.'

export const INSIDE_FOOTNOTE: string[] = [
  'Real pages.',
  'Clear instructions.',
  'Practical tools you can keep open while you work.',
]

/** Real pages from the product, in the exact order supplied. Shown uncropped. */
export type InsidePage = { caption: string; src: string; alt: string }

export const insidePages: InsidePage[] = [
  {
    caption: 'Customer Order Form',
    src: 'https://cdn.magicpatterns.com/uploads/piAzWzWrBPB4pn1W2EkcNz/Diseno_sin_titulo_(11).png',
    alt: 'Customer Order Form printable with order information, payment and delivery, product customization, notes and order status',
  },
  {
    caption: 'Custom Order Intake Message',
    src: 'https://cdn.magicpatterns.com/uploads/nhNX4XM98GTaNAwhAMML8g/Diseno_sin_titulo_(12).png',
    alt: 'The Simple Custom Order Intake Message — a copy and paste template with ten fill-in fields',
  },
  {
    caption: 'What Does One Piece Really Cost?',
    src: 'https://cdn.magicpatterns.com/uploads/v1pHwz9KbyjZcRVVFbHL8j/Diseno_sin_titulo_(13).png',
    alt: 'Know your cost page listing resin, colorant, inclusions, hardware, mold use, consumables, packaging, labor, fees, waste and overhead',
  },
  {
    caption: 'Resin Fix — Sticky Surface',
    src: 'https://cdn.magicpatterns.com/uploads/9er9R7cCYKrHqoT9549JMz/Diseno_sin_titulo_(14).png',
    alt: 'Sticky Surface troubleshooting page with what you see, possible causes, what to check first and outcome classification',
  },
  {
    caption: 'Design Recipes',
    src: 'https://cdn.magicpatterns.com/uploads/cunTp846MWMBKPHUt3Pevb/Diseno_sin_titulo_(15).png',
    alt: 'Design recipes Black Gold Clear and Ivory Pearl Gold with palette, pour order and best uses',
  },
  {
    caption: 'Project 01 — Initial Keychain',
    src: 'https://cdn.magicpatterns.com/uploads/gum8eZzfUtxfV2Yz6Cq3Jr/Diseno_sin_titulo_(16).png',
    alt: 'Project 01 Personalized Initial Keychain with materials and four illustrated steps',
  },
  {
    caption: 'Start With the Material',
    src: 'https://cdn.magicpatterns.com/uploads/vYFE9PLALKRA1xqekLswL6/Diseno_sin_titulo_(17).png',
    alt: 'Start With the Material page explaining how two-part epoxy resin and hardener cure, with Resin Rule 01',
  },
  {
    caption: '20 Step-by-Step Projects',
    src: 'https://cdn.magicpatterns.com/uploads/1LBPjRNULoNYrJTLSPvDn3/Diseno_sin_titulo_(18).png',
    alt: 'Overview of all 20 resin projects including keychains, pens, bookmarks, earrings, pendants and pet tags',
  },
  {
    caption: '50 Design Recipes',
    src: 'https://cdn.magicpatterns.com/uploads/9egDjXVfcobv5BV9PaAkip/Diseno_sin_titulo_(19).png',
    alt: 'Visual recipe library showing numbered resin color and inclusion designs',
  },
]

/* -------------------------------------------------------- Mid-page CTA */

export const MID_TITLE_1 = 'You already know the kind of things you want to make.'
export const MID_TITLE_2 = 'This is the system that helps you actually start.'

export const midHighlights: string[] = [
  '6 complete volumes',
  '20 step-by-step projects',
  '50 design recipes',
  'Troubleshooting',
  'Pricing',
  'Business tools',
  'Customer scripts',
]

export const MID_CLOSING =
  'From your first pour to a more organized way to create and sell your work.'

/* --------------------------------------------------------------- Bonus */

export const bonus = {
  title: 'Custom Order Client Kit',
  subtitle: 'Ready-to-use scripts & customer templates for personalized resin orders',
  hookLead: 'When somebody finally sends:',
  hookMessage: 'Hi! How much is this?',
  hookTail: 'you shouldn’t have to figure out what to say next.',
  listIntro: 'Includes ready-to-use communication for:',
  items: [
    'First inquiries',
    'Custom-order details',
    'Personalization approval',
    'Pricing and payment messages',
    'Production updates',
    'Changes and delays',
    'Event and bulk orders',
    'Shipping and delivery',
    'Customer follow-up',
  ],
  micro: ['Copy.', 'Customize.', 'Send.'],
  support: 'Spend less time wondering what to say and more time creating.',
  value: '$27 USD',
}

/* --------------------------------------------------------------- Offer */

export const OFFER_TITLE = 'Your complete Resin to Profit™ system'

export const offerVolumes: string[] = [
  'Volume 01 — The Resin Starter Guide',
  'Volume 02 — The Resin Project Lab',
  'Volume 03 — Resin Design Recipes',
  'Volume 04 — Resin Fix',
  'Volume 05 — The Resin Business Blueprint',
  'Volume 06 — Business Tools & Printables',
]

export const offerExtras: string[] = [
  '20 step-by-step projects',
  '50 design recipes',
  'Troubleshooting reference',
  'Pricing & costing guidance',
  'Business printables',
  'Customer scripts',
  '100% digital',
  'Instant access',
]

/* ---------------------------------------------------------- Comparison */

export type ComparisonRow = { aspect: string; without: string; with: string }

export const comparisonRows: ComparisonRow[] = [
  { aspect: 'Learning', without: 'Random videos', with: 'One organized process' },
  { aspect: 'Materials', without: '“What do I need?”', with: 'Know what information to check' },
  { aspect: 'Projects', without: 'No idea what to make', with: '20 projects to follow' },
  { aspect: 'Design', without: 'Random colors and saved inspiration', with: '50 design recipes to explore' },
  { aspect: 'Problems', without: 'Something goes wrong', with: 'Troubleshooting reference' },
  { aspect: 'Pricing', without: 'Guessing', with: 'Cost and pricing framework' },
  { aspect: 'Organization', without: 'Orders everywhere', with: 'Business tools and printables' },
  { aspect: 'Customers', without: '“What do I tell them?”', with: 'Ready-to-use client scripts' },
]

/* ----------------------------------------------------------- Guarantee */

export const guaranteeSteps: string[] = [
  'Open the volumes.',
  'Explore the projects.',
  'Use the tools.',
]

export const GUARANTEE_BODY =
  'If within 7 days you decide Resin to Profit™ isn’t what you expected, request a refund.'

/* ----------------------------------------------------------------- FAQ */

export type Faq = { question: string; answer: string }

export const faqs: Faq[] = [
  {
    question: 'Is this for complete beginners?',
    answer:
      'Yes. The system starts with the fundamentals and progresses through projects, design, troubleshooting, pricing and business organization.',
  },
  {
    question: 'Do I need resin experience?',
    answer: 'No. It was designed so someone starting from zero can understand the process.',
  },
  {
    question: 'Do I need professional equipment?',
    answer:
      'No. You can begin learning the system before investing in advanced equipment or building a professional workshop.',
  },
  {
    question: 'Is this a video course?',
    answer:
      'No. Resin to Profit™ is a complete PDF-based digital system designed to be easy to reference while you work.',
  },
  {
    question: 'How do I receive the material?',
    answer: 'Digital access is provided after purchase.',
  },
  {
    question: 'Can I print the guides?',
    answer: 'Yes. You can use the PDFs digitally or print them for personal use.',
  },
  {
    question: 'What does the bonus include?',
    answer:
      'The Custom Order Client Kit includes customer communication scripts and templates for personalized resin orders.',
  },
  {
    question: 'Does this guarantee I will make money?',
    answer:
      'No. Resin to Profit™ teaches creation, costing, pricing and business organization. Results depend on your products, market and execution.',
  },
]

/* --------------------------------------------------------------- Close */

export const CLOSING_LINE =
  'Your next resin project doesn’t have to start with another random video.'

export const CLOSING_SUB_1 = 'You already have the ideas.'
export const CLOSING_SUB_2 = 'Now you need the system.'

export const FINAL_SUBLINE = '6 complete volumes + Custom Order Client Kit'

/* -------------------------------------------------------------- Footer */

export const footerTagline: string[] = [
  'Practical ideas.',
  'Clear systems.',
  'Skills you can actually use.',
]

/* ------------------------------------------------------- Purchase toast */

export type PurchaseNotice = { name: string; location: string; minutesAgo: number }

/** Editable placeholder. */
export const purchaseNotice: PurchaseNotice = {
  name: 'Emily R.',
  location: 'Austin, TX',
  minutesAgo: 3,
}
